// components/PaymentDetails.js
import React, { useState, useEffect } from 'react';
import { Modal, Table, Tooltip } from 'antd'
import moment from 'moment'
import { Text } from './Text';
import { Flex, FlexColumn, CardContainer, LightContainer } from './Container'
import { toCurrency, getBPAccountsWithAccess, getRailPaymentInfo, railCutOffTxt } from '../../Utils/util'
import ModalClose from '../../Images/modal-close.png'
import ToolTip from './Tooltip';

const dFormat = "ll; h:mm A z"
const etTimeZone = "America/New_York";

const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
})

const PaymentDetails = ({ paymentMethod, dailyLimits, feeInfo, sameDay, samedayACHFee, theme, width, miniVersion, ctaMode, limitCta }) => {
    const [details, setDetails] = useState({});
    const [showPendingModal, setShowPendingModal] = useState(false);
    const [modalTitle, setModalTitle] = useState("");

    useEffect(() => {
        if (!paymentMethod || !paymentMethod.type) {
            console.log("Payment method not set or invalid");
            return;
        }

        const methodDetails = getRailPaymentInfo(dailyLimits, feeInfo, samedayACHFee);
        setDetails(methodDetails[paymentMethod.type] || {});
    }, [paymentMethod, dailyLimits, feeInfo, sameDay, samedayACHFee]);

    const handleShowPending = (type) => {
        setModalTitle(type);
        setShowPendingModal(true);
    };

    const closeModal = () => {
        setShowPendingModal(false);
    };

    var Accounts = getBPAccountsWithAccess("Payments", { permType: "manage", getActiveAccounts: true })

    const pendingColumns = [{
        title: 'Date',
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (createdAt, record) => {
            return <Text>{moment.utc(createdAt).tz(etTimeZone).format(dFormat)}</Text>
        }
    }, {
        title: 'From Account',
        dataIndex: 'accountNumber',
        key: 'accountNumber',
        render: (accountNumber, record) => {
            var account = Accounts.find(acc => acc.accountNumber === accountNumber)
            return <Text capitalize>{account ? `${(account.nickName || `Business ${account.accountSubType}`)} • ${account.mask}` : '--'}</Text>
        }
    }, {
        title: 'Recipient',
        dataIndex: 'recipient',
        key: 'recipient',
    },
    {
        title: 'Amount',
        dataIndex: 'amount',
        key: 'Amount',
        align: "right",
        render: (amount, record) => {
            if (record.dateRow === true) {
                return { props: { colSpan: 0 } }
            } else {
                switch (record.transactionType) {
                    case 'credit':
                        return <Text weight='600' credit>+ {formatter.format(amount)}</Text>
                    case 'debit':
                        return <Text weight='600' color={theme.colors.defaultLightText}>- {formatter.format(Math.abs(amount))}</Text>
                    default:
                        return <Text weight='600' color={theme.colors.defaultLightText}>{formatter.format(Math.abs(amount))}</Text>
                }
            }
        },
        width: 140,
    }, {
        title: 'Initiated By',
        dataIndex: 'initiatedBy',
        key: 'initiatedBy',
    },]

    if (paymentMethod.type === 'ACH') {
        pendingColumns.push({
            title: 'Delivery Type',
            dataIndex: 'deliveryType',
            key: 'deliveryType',
        })
    }

    if (!paymentMethod || !dailyLimits) return null;

    const cutoffTxt = railCutOffTxt(paymentMethod, details, sameDay);

    return (
        <>
            {   
                !limitCta &&
                <FlexColumn start gap='12px' style={{ margin: !miniVersion && "12px 0", backgroundColor: theme.colors.backgroundBanner, padding: "12px 24px", borderRadius: "8px", width: width || 1176 }}>
                    {
                        !miniVersion &&
                        <Text lightText>
                            {cutoffTxt}
                        </Text>
                    }
                    <Flex start wrap style={{ gap: "24px", marginTop: 8 }}>
                        <Text caption>
                            Today's Remaining Limit:
                            {/* {details.remainingBalance
                                ? <span style={{ color: theme.colors.blackPrimary }}> ${toCurrency(details.remainingBalance)}</span>
                                : ( */}
                            <Text underline margin='0 0 0 8px' onClick={() => handleShowPending(details.pendingHandler)}>
                                ${toCurrency(details.remainingBalance)}
                            </Text>
                            {/* )} */}
                        </Text>
                        {
                            !miniVersion ?
                            <Text caption>
                                {sameDay ? "Same Day ACH Fees:" : `${paymentMethod.type}${paymentMethod.type === 'Instant' ? ' Transfer' : ''} Fees:`}
                                <span style={{ color: theme.colors.blackPrimary }}> ${toCurrency(sameDay ? samedayACHFee : details.fee)}</span>
                            </Text>
                            :
                            <Text caption>
                                Transfer Fees:
                                <span style={{ color: theme.colors.blackPrimary }}> ${toCurrency(details.fee)}</span>
                            </Text>
                        }
                        {
                            miniVersion &&
                            <Flex centerH gap='4px'>
                                <Text caption>
                                    Cut-off:
                                </Text>
                                {details.cutoff}
                                <ToolTip desc={cutoffTxt} />
                            </Flex>
                        }
                    </Flex>
                </FlexColumn>
            }

            {
                limitCta &&
                <FlexColumn onClick={() => handleShowPending(details.pendingHandler)}>
                    {limitCta}
                </FlexColumn>
            }

            <Modal
                visible={showPendingModal}
                footer={null}
                closable={false}
                width={1200}
                destroyOnClose={true}
                onCancel={closeModal}
                closeIcon={<img width='24px' height='24px' src={ModalClose} />}
            >
                {dailyLimits && (
                    <FlexColumn>
                        <Text heading color='black'>Pending {modalTitle} Transfers</Text>
                        {modalTitle === 'ACH' && (
                            <Text margin='0 0 12px'>{`$${toCurrency(dailyLimits.dailyLimit - dailyLimits.dailyAvailableACHBalance)} utilized of $${toCurrency(dailyLimits.dailyLimit)}`}</Text>
                        )}
                        {modalTitle === 'Wire' && (
                            <Text margin='0 0 12px'>{`$${toCurrency(dailyLimits.dailyLimit - dailyLimits.dailyAvailableWireBalance)} utilized of $${toCurrency(dailyLimits.dailyLimit)}`}</Text>
                        )}
                        {modalTitle === 'Check' && (
                            <Text margin='0 0 12px'>{`$${toCurrency(dailyLimits.dailyLimit - dailyLimits.dailyAvailableCheckBalance)} utilized of $${toCurrency(dailyLimits.dailyLimit)}`}</Text>
                        )}
                        {modalTitle === 'Instant' && (
                            <Text margin='0 0 12px'>{`$${toCurrency(dailyLimits.dailyLimit - dailyLimits.dailyAvailableInstantXferBalance)} utilized of $${toCurrency(dailyLimits.dailyLimit)}`}</Text>
                        )}
                        <Table
                            id="pending-table"
                            className='borderless-table'
                            pagination={false}
                            tableLayout='auto'
                            columns={pendingColumns}
                            dataSource={dailyLimits.txnDetails}
                            rowKey='objectId'
                        />
                    </FlexColumn>
                )}
            </Modal>
        </>
    );
};

export default PaymentDetails;